import type { IJourney } from '../models/journey.model';
import type { ITripLeg } from '../models/trip-leg.model';
import { LegHelper } from './leg.helper';
import LinkEndpointHelper from './link-endpoint.helper';
import { type IOccupancy } from '../models/occupancy.model';
import type { INote } from '../models/note.model';
import { DateTime } from 'luxon';
import { DateTimeHelper } from './date-time.helper';
import type { TransportMode } from '../models/transport-mode.enum';
import type { ILinkEndpoint } from '../models/link-endpoint.model';
import type { IStopPoint } from '../models/stop-point.model';
import { type ILocationAutocompleteSelection } from '../components/Shared/LocationAutocomplete';
import { LocationAutocompleteSelectionType } from '../components/Shared/LocationAutocomplete/location-autocomplete-selection-type.enum';
import { LocationType } from '../models/location-type.enum';

export class JourneyHelper {
  public static setJourneyIds(journeys: IJourney[], startIndex: number, negative: boolean): IJourney[] {
    for (let i = 0; i < journeys.length; i += 1) {
      journeys[i].id = negative ? startIndex - journeys.length + 1 + i : startIndex + i;
    }
    return journeys;
  }

  public static getFirstPublicTransportLeg(journey: IJourney | undefined): ITripLeg | undefined {
    if (!journey || !journey.tripLegs || journey.tripLegs.length < 1) {
      return undefined;
    }
    return journey.tripLegs[0];
  }

  public static getLastPublicTransportLeg(journey: IJourney): ITripLeg | undefined {
    if (!journey.tripLegs || journey.tripLegs.length < 1) {
      return undefined;
    }
    return journey.tripLegs.slice().reverse()[0];
  }

  public static getAllNotes(journey: IJourney): INote[] {
    let allNotes: INote[] = [];
    if (journey.destinationLink) {
      allNotes = ([] as INote[])
        .concat(journey.destinationLink.notes || [])
        .concat(journey.destinationLink.origin.notes || [])
        .concat(journey.destinationLink.destination.notes || []);
    }
    if (journey.allLegs) {
      journey.allLegs.forEach((tripLeg) => {
        allNotes = allNotes.concat(LegHelper.getAllNotes(tripLeg));
      });
    }
    return allNotes;
  }

  public static getTripLegIsCancelled(journey: IJourney): boolean {
    if (!journey.tripLegs || journey.tripLegs.length < 1) {
      return false;
    }
    return journey.tripLegs.some((element) => element.isCancelled);
  }

  public static getTripLegIsPartCancelled(journey: IJourney): boolean {
    if (!journey.tripLegs || journey.tripLegs.length < 1) {
      return false;
    }
    return journey.tripLegs.some((element) => element.isPartCancelled);
  }

  public static isDepartureOnTime(journey: IJourney): boolean {
    if (journey.destinationLink) {
      return LinkEndpointHelper.isOnTime(journey.destinationLink.origin);
    }
    if (!journey.allLegs || journey.allLegs.length < 1) {
      return true;
    }
    if (journey.departureAccessLink) {
      return LinkEndpointHelper.isOnTime(journey.departureAccessLink.origin);
    }
    return LegHelper.isDepartureOnTime(journey.allLegs[0]);
  }

  public static isArrivalOnTime(journey: IJourney): boolean {
    if (journey.destinationLink) {
      return LinkEndpointHelper.isOnTime(journey.destinationLink.destination);
    }
    if (!journey.allLegs || journey.allLegs.length < 1) {
      return true;
    }
    if (journey.arrivalAccessLink) {
      return LinkEndpointHelper.isOnTime(journey.arrivalAccessLink.destination);
    }
    return LegHelper.isArrivalOnTime(journey.allLegs[journey.allLegs.length - 1]);
  }

  public static getDepartureTimeForJourney(journey: IJourney, useEstimatedTime: boolean): DateTime | undefined {
    if (journey.destinationLink) {
      const departureDestinationLinkTime = LinkEndpointHelper.getTime(journey.destinationLink.origin, useEstimatedTime);
      if (departureDestinationLinkTime) {
        return departureDestinationLinkTime;
      }
    }

    if (journey.departureAccessLink) {
      const departureAccessLinkTime = LinkEndpointHelper.getTime(journey.departureAccessLink.origin, useEstimatedTime);
      if (departureAccessLinkTime) {
        return departureAccessLinkTime;
      }
    }

    if (!journey.allLegs || journey.allLegs.length < 1) {
      return undefined;
    }

    return LegHelper.getDepartureTime(journey.allLegs[0], useEstimatedTime);
  }

  public static getArrivalTimeForJourney(journey: IJourney, useEstimatedTime: boolean): DateTime | undefined {
    if (journey.destinationLink) {
      const arrivalDestinationLinkTime = LinkEndpointHelper.getTime(journey.destinationLink.destination, useEstimatedTime);
      if (arrivalDestinationLinkTime) {
        return arrivalDestinationLinkTime;
      }
    }

    if (journey.arrivalAccessLink) {
      const arrivalAccessLinkTime = LinkEndpointHelper.getTime(journey.arrivalAccessLink.destination, useEstimatedTime);
      if (arrivalAccessLinkTime) {
        return arrivalAccessLinkTime;
      }
    }

    if (!journey.allLegs || journey.allLegs.length < 1) {
      return undefined;
    }

    return LegHelper.getArrivalTime(journey.allLegs[journey.allLegs.length - 1], useEstimatedTime);
  }

  public static getDurationInMinutes(journey: IJourney, useEstimatedTime: boolean): number {
    if (!journey.allLegs && !journey.destinationLink) {
      return 0;
    }
    const timeAtOrigin = this.getDepartureTimeForJourney(journey, useEstimatedTime);
    const timeAtDestination = this.getArrivalTimeForJourney(journey, useEstimatedTime);
    if (!timeAtOrigin || !timeAtDestination) {
      return 0;
    }
    return DateTimeHelper.minutes(timeAtOrigin, timeAtDestination);
  }

  public static getNumberOfInterchanges(journey: IJourney): number {
    if (!journey.tripLegs || journey.tripLegs.length < 2) {
      return 0;
    }
    return journey.tripLegs.length - 1;
  }

  public static getJourneyWheelchairAccessibility(journey: IJourney): boolean {
    if (!journey.tripLegs || journey.tripLegs.length < 1) {
      return false;
    }
    return journey.tripLegs.every((x) => x.serviceJourney.line && x.serviceJourney.line.isWheelchairAccessible);
  }

  public static getJourneyOccupancy(journey: IJourney): IOccupancy | undefined {
    return journey.occupancy;
  }

  public static isWalkJourney(journey: IJourney): boolean {
    return journey.destinationLink !== undefined;
  }

  public static getAllUniqueDates(journeys: IJourney[]): DateTime[] {
    const dates = journeys
      .map((x) => JourneyHelper.getDepartureTimeForJourney(x, true))
      .filter((x) => x !== undefined)
      .map((x) => x.startOf('day'));
    return dates.filter((x, i) => dates.findIndex((y) => DateTimeHelper.isSameDay(x, y)) === i);
  }

  public static getJourneysForDate(journeys: IJourney[], date: DateTime): IJourney[] {
    return journeys.filter((x) => DateTimeHelper.isSameDay(JourneyHelper.getDepartureTimeForJourney(x, true), date));
  }

  public static getTransportModesForJourneys(journeys: IJourney[]): TransportMode[] {
    const transportModes = journeys
      .flatMap((journey) => journey.tripLegs ?? [])
      .map((tripLeg) => tripLeg.serviceJourney.line?.transportMode)
      .filter((transportMode): transportMode is TransportMode => transportMode !== undefined);
    return Array.from(new Set(transportModes));
  }

  public static resolveFromToLocations(journey: IJourney): { fromLocation: ILocationAutocompleteSelection | undefined; toLocation: ILocationAutocompleteSelection | undefined } {
    const firstTripLeg = journey.tripLegs?.[0];
    const lastTripLeg = journey.tripLegs?.[journey.tripLegs.length - 1];

    function mapToLocationSelection(linkEndpoint: ILinkEndpoint | undefined, stopPoint: IStopPoint | undefined, platform?: string): ILocationAutocompleteSelection | undefined {
      const hasCoordinates = (latitude?: number, longitude?: number): boolean => latitude !== undefined && longitude !== undefined;

      if (linkEndpoint?.gid)
        return {
          type: LocationAutocompleteSelectionType.location,
          location: {
            gid: linkEndpoint.gid,
            name: linkEndpoint.name,
            locationType: linkEndpoint.locationType,
            latitude: linkEndpoint.latitude,
            longitude: linkEndpoint.longitude,
            hasLocalService: false,
          },
        };

      if (linkEndpoint && hasCoordinates(linkEndpoint.latitude, linkEndpoint.longitude) && linkEndpoint.locationType !== LocationType.stopPoint)
        return {
          type: LocationAutocompleteSelectionType.currentPosition,
          coordinates: { latitude: linkEndpoint.latitude!, longitude: linkEndpoint.longitude! },
        };

      if (stopPoint)
        return {
          type: LocationAutocompleteSelectionType.location,
          location: { gid: stopPoint.gid, name: stopPoint.name, locationType: LocationType.stopPoint, latitude: stopPoint.latitude, longitude: stopPoint.longitude, platform, hasLocalService: false },
        };
      return undefined;
    }

    return {
      fromLocation: mapToLocationSelection(journey.destinationLink?.origin ?? journey.departureAccessLink?.origin, firstTripLeg?.origin.stopPoint, firstTripLeg?.origin.stopPoint?.platform),
      toLocation: mapToLocationSelection(
        journey.destinationLink?.destination ?? journey.arrivalAccessLink?.destination,
        lastTripLeg?.destination.stopPoint,
        lastTripLeg?.destination.stopPoint?.platform,
      ),
    };
  }
}

